import { Box, Divider, List, Paper } from '@mui/material';
import { FieldValues, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import {
  LuBuilding,
  LuClock,
  LuFileClock,
  LuGlobe,
  LuGraduationCap,
  LuLanguages,
  LuLoader,
  LuNavigation,
  LuPlane,
  LuSearch,
  LuStamp,
  LuUsers,
} from 'react-icons/lu';
import { TbBriefcase } from 'react-icons/tb';
import { WsColors, WsInputNumber } from 'wisestep-base';

import CheckboxFilter from '../../../../components/filters/components/CheckboxFilter';
import CheckboxListFilter from '../../../../components/filters/components/CheckboxListFilter';
import MultiMatchFilter from '../../../../components/filters/components/MultiMatchFilter';
import RadioListFilter from '../../../../components/filters/components/RadioListFilter';
import SalaryRangeFilter from '../../../../components/filters/components/SalaryRangeFilter';
import WsAccordion from '../../../../components/filters/components/WsAccordion';
import AdvancedFiltersHeader from '../../../../utils/AdvancedFiltersHeader';
import ExperienceRangeFilter from '../../components/ExperienceRangeFilter';
import BasicSearchForm from '../../types/IBasicSearchForm';
import CareerBuilderAdvanceSearch from '../types/ICareerBuilderAdvanceSearch';
import {
  authorizedCountriesCheckBoxOptions,
  CompensationOptions,
  currentlyEmployedOptions,
  employementTypeCheckBoxOptions,
  hasSecurityClearanceOptions,
  languaagesCheckBoxOptions,
  managementExperienceOptions,
  militaryExperienceCheckBoxOptions,
  miniDegreeCheckBoxOptions,
  searchPatternOptions,
  travelPreferenceOptions,
  workAuthorizationCheckBoxOptions,
} from '../utils/CareerBuilderHelper';

interface AdvanceFilterNewProps {
  performSearch: (values: BasicSearchForm & CareerBuilderAdvanceSearch) => void;
}
const AdvanceFilterNew = ({ performSearch }: AdvanceFilterNewProps) => {
  const methods = useFormContext();
  const { handleSubmit, reset } = methods;
  return (
    <FormProvider {...methods}>
      <Paper sx={{ mt: 2 }}>
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(performSearch as SubmitHandler<FieldValues>)}
          onReset={() => reset()}
        >
          <AdvancedFiltersHeader />
          <Divider />
          <List>
            <RadioListFilter
              name="searchPattern"
              row={false}
              options={searchPatternOptions}
              title="Search Pattern"
              startIcon={<LuSearch color={WsColors.TertiaryShades800} />}
            />
            <ExperienceRangeFilter />
            <MultiMatchFilter
              name="jobTitle.values"
              title="Job Title"
              placeholder="Job Titles"
              matchTypeFieldName="jobTitle.matchType"
              startIcon={<TbBriefcase color={WsColors.TertiaryShades800} />}
            />
            <MultiMatchFilter
              name="companies.values"
              title="Companies"
              placeholder="Companies"
              matchTypeFieldName="companies.matchType"
              startIcon={<LuBuilding color={WsColors.TertiaryShades800} />}
            />

            <CheckboxListFilter
              name="minimumDegree"
              options={miniDegreeCheckBoxOptions}
              title="Minimum Degree"
              startIcon={<LuGraduationCap color={WsColors.TertiaryShades800} />}
            />

            <CheckboxListFilter
              name="languages"
              options={languaagesCheckBoxOptions}
              title="Languages"
              startIcon={<LuLanguages color={WsColors.TertiaryShades800} />}
            />
            <CheckboxFilter
              name="applyLastActivity"
              title="Last Active"
              label="Last Active"
              startIcon={<LuLoader color={WsColors.TertiaryShades800} />}
            />

            <CheckboxListFilter
              name="employmentType"
              options={employementTypeCheckBoxOptions}
              title="Employment Type"
              startIcon={<LuFileClock color={WsColors.TertiaryShades800} />}
            />

            <CheckboxListFilter
              name="authorizedCountries"
              options={authorizedCountriesCheckBoxOptions}
              title="Countries"
              startIcon={<LuGlobe color={WsColors.TertiaryShades800} />}
            />

            <CheckboxListFilter
              name="workAuthorization"
              options={workAuthorizationCheckBoxOptions}
              title="Work Authorization"
              startIcon={<LuPlane color={WsColors.TertiaryShades800} />}
            />

            <SalaryRangeFilter
              title="Compensation"
              selectArray={[]}
              selectArray1={CompensationOptions}
              showAccordionIcon={true}
            />

            <RadioListFilter
              name="hasSecurityClearance"
              row={false}
              options={hasSecurityClearanceOptions}
              title="Has Security Clearance"
              startIcon={<LuStamp color={WsColors.TertiaryShades800} />}
            />

            <RadioListFilter
              name="currentlyEmployed"
              row={false}
              options={currentlyEmployedOptions}
              title="Currently Employed"
              startIcon={<TbBriefcase color={WsColors.TertiaryShades800} />}
            />

            <RadioListFilter
              name="managementExperience"
              row={false}
              options={managementExperienceOptions}
              title="Management Experience"
              startIcon={<LuClock color={WsColors.TertiaryShades800} />}
            />

            <WsAccordion
              title={'Employees Managed'}
              startIcon={<LuUsers color={WsColors.TertiaryShades800} />}
            >
              <Box>
                <WsInputNumber name="minEmployeeManaged" label="Min" />
              </Box>
            </WsAccordion>

            <CheckboxListFilter
              name="militaryExperience "
              options={militaryExperienceCheckBoxOptions}
              title="Military Experience "
              startIcon={<LuClock color={WsColors.TertiaryShades800} />}
            />

            <RadioListFilter
              name="travelPreference"
              row={false}
              options={travelPreferenceOptions}
              title="Travel Preference"
              startIcon={<LuNavigation color={WsColors.TertiaryShades800} />}
            />
          </List>
        </Box>
      </Paper>
    </FormProvider>
  );
};

export default AdvanceFilterNew;
